import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography, TextField, Button, Avatar, Checkbox, Switch, IconButton } from '@mui/material';

// ============================================
// Page Layout (좌우 2단)
// ============================================

export const PageContainer = styled(Box)({
  display: 'flex',
  gap: '32px',
  alignItems: 'flex-start',
  padding: '32px 0',
  width: '100%',
});

export const LeftColumn = styled(Box)({
  width: '460px',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  height: 'calc(100vh - 140px)',
  borderRight: '1px solid #E8E8E8',
  paddingRight: '32px',
});

export const RightColumn = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

// ============================================
// LEFT: 검색 + 퇴사자 제외
// ============================================

export const SearchCard = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: '#fff',
  border: '1px solid #D9D9D9',
  borderRadius: '8px',
  padding: '16px',
  height: '88px',
  boxSizing: 'border-box',
});

export const SearchInput = styled(TextField)({
  width: '280px',
  '& .MuiOutlinedInput-root': {
    height: '44px',
    borderRadius: '8px',
    background: '#fff',
    '& fieldset': { borderColor: '#D9D9D9' },
    '&:hover fieldset': { borderColor: '#4AB3BC' },
    '&.Mui-focused fieldset': { borderColor: '#4AB3BC' },
  },
  '& input': {
    padding: '8px 16px',
    fontSize: '16px',
    fontFamily: 'Pretendard, sans-serif',
    fontWeight: 500,
    '&::placeholder': {
      color: '#A6A6A6',
      opacity: 1,
    },
  },
});

export const ExcludeRetiredRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  userSelect: 'none',
});

export const ExcludeRetiredLabel = styled(Typography)({
  color: '#262626',
  fontSize: '16px',
  fontWeight: 500,
  fontFamily: 'Pretendard, sans-serif',
});

// ============================================
// LEFT: 직급 아코디언
// ============================================

export const RoleListWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  border: '1px solid #E8E8E8',
  borderRadius: '8px',
  padding: '16px',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#c1c1c1',
    borderRadius: '10px',
    '&:hover': {
      backgroundColor: '#a8a8a8',
    },
  },
});

export const RoleAccordion = styled(Box, {
  shouldForwardProp: prop => prop !== 'expanded',
})(({ expanded }) => ({
  background: expanded ? '#F3F9FA' : '#fff',
  border: `1px solid ${expanded ? '#4AB3BC' : '#D9D9D9'}`,
  borderRadius: '8px',
  padding: expanded ? '16px' : '10px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  transition: 'all 0.15s ease',
}));

export const RoleAccordionHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 8px',
  cursor: 'pointer',
  minHeight: '28px',
});

export const RoleTitle = styled(Typography)({
  color: '#333',
  fontSize: '20px',
  fontWeight: 600,
  fontFamily: 'Pretendard, sans-serif',
});

export const RoleChevron = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    fontSize: 25,
    color: '#595959',
  },
});

// ============================================
// LEFT: 임직원 항목
// ============================================

export const StaffList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const StaffItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected',
})(({ selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  background: '#fff',
  border: `1px solid ${selected ? '#4AB3BC' : '#F2F2F2'}`,
  borderRadius: '8px',
  padding: '16px',
  cursor: 'pointer',
  transition: 'border-color 0.15s ease',
  '&:hover': {
    borderColor: '#4AB3BC',
  },
}));

export const StaffAvatar = styled(Avatar)({
  width: 50,
  height: 50,
});

export const StaffInfo = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  minWidth: 0,
});

export const StaffName = styled(Typography)({
  color: '#737373',
  fontSize: '16px',
  fontWeight: 500,
  fontFamily: 'Pretendard, sans-serif',
});

export const StaffPhone = styled(Typography)({
  color: '#737373',
  fontSize: '16px',
  fontWeight: 500,
  fontFamily: 'Pretendard, sans-serif',
});

export const StaffDetailButton = styled(Button)({
  color: '#4AB3BC',
  fontSize: '14px',
  fontWeight: 600,
  fontFamily: 'Pretendard, sans-serif',
  padding: '4px 8px',
  borderRadius: '2px',
  textTransform: 'none',
  minWidth: 0,
  whiteSpace: 'nowrap',
});

export const StaffLockIcon = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    fontSize: 24,
    color: '#737373',
  },
});

// ============================================
// RIGHT: 권한 카테고리 탭
// ============================================

export const PermissionTabsContainer = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
});

export const PermissionTabCell = styled(Box, {
  shouldForwardProp: prop => prop !== 'active' && prop !== 'empty',
})(({ active, empty }) => ({
  flex: '0 0 25%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '44px',
  cursor: empty ? 'default' : 'pointer',
  color: empty ? 'transparent' : active ? '#4AB3BC' : '#595959',
  fontSize: '16px',
  fontWeight: 600,
  fontFamily: 'Pretendard, sans-serif',
  borderBottom: empty
    ? '1px solid #D9D9D9'
    : active
    ? '2px solid #4AB3BC'
    : '1px solid #D9D9D9',
  userSelect: 'none',
  transition: 'all 0.15s ease',
  '&:hover': empty
    ? {}
    : {
        color: active ? '#4AB3BC' : '#262626',
      },
}));

// ============================================
// RIGHT: 섹션 타이틀
// ============================================

export const SectionTitle = styled(Box)({
  color: '#262626',
  fontSize: '20px',
  fontWeight: 600,
  fontFamily: 'Pretendard, sans-serif',
  padding: '8px 0',
  minHeight: '64px',
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
});

export const SectionTitleToggles = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
});

export const ToggleItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  cursor: 'pointer',
  userSelect: 'none',
});

export const ToggleLabel = styled(Typography)({
  fontSize: '15px',
  fontWeight: 500,
  color: '#555',
  fontFamily: 'Pretendard, sans-serif',
  whiteSpace: 'nowrap',
});

export const PasswordChangeButton = styled(Button)({
  fontSize: '13px',
  fontWeight: 500,
  fontFamily: 'Pretendard, sans-serif',
  color: '#4AB3BC',
  border: '1px solid #4AB3BC',
  borderRadius: '6px',
  padding: '4px 12px',
  minWidth: 0,
  whiteSpace: 'nowrap',
  textTransform: 'none',
  '&:hover': {
    background: '#F3F9FA',
    border: '1px solid #4AB3BC',
  },
});

// ============================================
// RIGHT: 권한 카드
// ============================================

export const PermissionCard = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  background: '#fff',
  border: '1px solid #F2F2F2',
  borderRadius: '8px',
  overflow: 'hidden',
});

export const PermissionCardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: '#F2F2F2',
  padding: '16px 24px',
});

export const PermissionCardTitle = styled(Typography)({
  color: '#262626',
  fontSize: '20px',
  fontWeight: 600,
  fontFamily: 'Pretendard, sans-serif',
});

export const SelectAllRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  userSelect: 'none',
});

export const SelectAllLabel = styled(Typography)({
  color: '#000',
  fontSize: '18px',
  fontWeight: 500,
  fontFamily: 'Pretendard, sans-serif',
});

export const PageViewRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '16px 24px',
  borderBottom: '1px solid #E8E8E8',
  background: '#FAFAFA',
});

export const PageViewLabel = styled(Typography)({
  color: '#262626',
  fontSize: '16px',
  fontWeight: 600,
  fontFamily: 'Pretendard, sans-serif',
});

export const PermissionRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '16px 24px',
});

export const PermissionCell = styled(Box)({
  flex: '1 0 0',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
});

export const PermissionClickArea = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  userSelect: 'none',
});

export const PermissionLabel = styled(Typography)({
  color: '#262626',
  fontSize: '16px',
  fontWeight: 500,
  fontFamily: 'Pretendard, sans-serif',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

// ============================================
// 체크박스 (24px 기본 / 32px 큰)
// ============================================

export const TealCheckbox = styled(Checkbox, {
  shouldForwardProp: prop => prop !== 'large',
})(({ large }) => ({
  padding: 0,
  color: '#A6A6A6',
  '&.Mui-checked': {
    color: '#4AB3BC',
  },
  '& .MuiSvgIcon-root': {
    fontSize: large ? 32 : 24,
  },
}));

// ============================================
// Footer Action Buttons
// ============================================

export const FooterBar = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '16px',
  marginTop: '8px',
});

const footerButtonBase = {
  height: '48px',
  borderRadius: '8px',
  fontSize: '20px',
  fontWeight: 600,
  fontFamily: 'Pretendard, sans-serif',
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
};

export const ResetButton = styled(Button)({
  ...footerButtonBase,
  width: '100px',
  background: '#D9D9D9',
  color: '#404040',
  '&:hover': {
    background: '#C9C9C9',
    boxShadow: 'none',
  },
});

export const ApplyAllButton = styled(Button)({
  ...footerButtonBase,
  width: '240px',
  background: '#fff',
  color: '#4AB3BC',
  border: '1px solid #4AB3BC',
  '&:hover': {
    background: '#F3F9FA',
    boxShadow: 'none',
    border: '1px solid #4AB3BC',
  },
});

export const SaveButton = styled(Button)({
  ...footerButtonBase,
  width: '240px',
  background: '#4AB3BC',
  color: '#fff',
  '&:hover': {
    background: '#3FA0A8',
    boxShadow: 'none',
  },
});

// ============================================
// 비밀번호 설정 모달
// ============================================

export const PwModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  background: '#fff',
  borderRadius: '12px',
  overflow: 'hidden',
  outline: 'none',
});

export const PwModalHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: '#333',
  padding: '14px 20px',
});

export const PwModalHeaderTitle = styled(Typography)({
  color: '#fff',
  fontSize: '16px',
  fontWeight: 600,
  fontFamily: 'Pretendard, sans-serif',
});

export const PwModalCloseButton = styled(IconButton)({
  color: '#fff',
  padding: '4px',
});

export const PwModalBody = styled(Box)({
  padding: '24px 24px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const PwModalSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

export const PwModalSectionLabel = styled(Typography)({
  color: '#262626',
  fontSize: '15px',
  fontWeight: 600,
  fontFamily: 'Pretendard, sans-serif',
});

export const PwToggleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
});

export const PwSwitch = styled(Switch)({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#4AB3BC',
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#4AB3BC',
  },
});

export const PwToggleText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#555',
  fontFamily: 'Pretendard, sans-serif',
});

export const PwModalDivider = styled(Box)({
  height: '1px',
  background: '#E8E8E8',
});

export const PwInput = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    height: '48px',
    borderRadius: '8px',
    '& fieldset': { borderColor: '#D9D9D9' },
    '&:hover fieldset': { borderColor: '#4AB3BC' },
    '&.Mui-focused fieldset': { borderColor: '#4AB3BC' },
  },
  '& input': {
    padding: '10px 16px',
    fontSize: '14px',
    fontFamily: 'Pretendard, sans-serif',
  },
});

export const PwVisibilityButton = styled(IconButton)({
  color: '#999',
  padding: '4px',
});

export const PwModalFooter = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  padding: '0 24px 24px',
});

// ============================================
// 권한 관리 가이드 버튼
// ============================================

const guideSlideIn = keyframes`
  0% { transform: translateX(30px); opacity: 0; }
  40% { transform: translateX(-5px); opacity: 1; }
  60% { transform: translateX(3px); }
  80% { transform: translateX(-1px); }
  100% { transform: translateX(0); }
`;

const guideShine = keyframes`
  0%, 100% { background-position: -200% center; }
  50% { background-position: 200% center; }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(74, 179, 188, 0.4); }
  50% { box-shadow: 0 0 0 6px rgba(74, 179, 188, 0); }
  100% { box-shadow: 0 0 0 0 rgba(74, 179, 188, 0); }
`;

export const GuideButton = styled(Button)({
  fontSize: '13px',
  fontWeight: 500,
  fontFamily: 'Pretendard, sans-serif',
  color: '#4AB3BC',
  border: '1px solid #4AB3BC',
  borderRadius: '20px',
  padding: '4px 14px',
  minWidth: 0,
  whiteSpace: 'nowrap',
  textTransform: 'none',
  background: 'linear-gradient(120deg, #4AB3BC 0%, #6DD5DC 40%, #fff 50%, #6DD5DC 60%, #4AB3BC 100%)',
  backgroundSize: '200% 100%',
  color: '#fff',
  border: '1px solid transparent',
  animation: `${guideSlideIn} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, ${guideShine} 3s ease-in-out 1s infinite, ${pulseGlow} 2s ease-in-out 1s 3`,
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'linear-gradient(120deg, #3FA0A8 0%, #4AB3BC 100%)',
    border: '1px solid transparent',
    transform: 'scale(1.05)',
    color: '#fff',
  },
});

// ============================================
// 도움말 텍스트
// ============================================

export const HelpText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 400,
  color: '#666',
  fontFamily: 'Pretendard, sans-serif',
  lineHeight: 1.6,
});

// ============================================
// 전체 적용 확인 모달
// ============================================

export const ApplyAllConfirmContent = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 360,
  background: '#F7F7F7',
  borderRadius: '16px',
  padding: '52px 36px 28px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '40px',
  outline: 'none',
});

export const ApplyAllConfirmText = styled(Typography)({
  fontSize: '20px',
  fontWeight: 500,
  color: '#333',
  textAlign: 'center',
  lineHeight: 1.8,
  fontFamily: 'Pretendard, sans-serif',
  '& span': {
    fontSize: '22px',
  },
});

export const ApplyAllConfirmButtons = styled(Box)({
  display: 'flex',
  gap: '10px',
  width: '100%',
});

const applyAllButtonBase = {
  flex: 1,
  height: '52px',
  borderRadius: '10px',
  fontSize: '18px',
  fontWeight: 500,
  fontFamily: 'Pretendard, sans-serif',
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
};

export const ApplyAllCancelButton = styled(Button)({
  ...applyAllButtonBase,
  background: '#ECECEC',
  color: '#666',
  '&:hover': { background: '#E0E0E0', boxShadow: 'none' },
});

export const ApplyAllSubmitButton = styled(Button)({
  ...applyAllButtonBase,
  background: '#333',
  color: '#fff',
  '&:hover': { background: '#444', boxShadow: 'none' },
});

// ============================================
// Empty State (직원 미선택)
// ============================================
export const EmptyRightPanel = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  minHeight: '400px',
});

export const EmptyRightText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#A6A6A6',
});
